// @ts-nocheck
import { faDownload, faLandmarkFlag } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	CheckCircleIcon,
	BanknotesIcon,
	ExclamationCircleIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Pagination from "../../components/pagination/Pagination";
import { RecordStatusBadge } from "../../components/status/RecordStatusBadge";
import {
	getRecordStatusLabel,
	isPendingRecordStatus,
	normalizeRecordStatus,
} from "../../utils/recordStatus";
import { URL } from "../../service/Service";
import WithdrawalFormAdmin from "./WithdrawalFormAdmin";

export const RecordsUserAdminPage = () => {
	const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);
	const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
	const [selectedPendingRecord, setSelectedPendingRecord] = useState(null);

	const [currentPage, setCurrentPage] = useState(1);
	const [originalPeople, setOriginalPeople] = useState([]);
	const [peopleData, setPeopleData] = useState([]);
	const [peoplePerPage] = useState(5);

	const [error, setError] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [approvalError, setApprovalError] = useState("");
	const [approvalSuccess, setApprovalSuccess] = useState("");
	const [approving, setApproving] = useState(false);

	const [searchTerm, setSearchTerm] = useState("");

	const jwtToken = sessionStorage.getItem("jwt");
	const { id } = useParams();

	const fetchRecords = useCallback(async () => {
		try {
			const response = await fetch(`${URL}/api/partners/${id}/records`, {
				headers: {
					Authorization: `Bearer ${jwtToken}`,
					"Content-Type": "application/json",
				},
			});

			if (response.status === 204) {
				setOriginalPeople([]);
				setPeopleData([]);
				setError(null);
				return;
			}

			if (!response.ok) {
				const text = await response.text();
				throw new Error(text || "Failed to fetch data");
			}

			const responseData = await response.json();

			const filteredData = Array.isArray(responseData)
				? responseData.filter(
					(item) => Number(item?.partnerRecordDTO?.recordAmount || 0) !== 0
				)
				: [];

			setOriginalPeople(filteredData);
			setPeopleData(filteredData);
			setError(null);
		} catch (error) {
			console.error("Erro ao carregar registos:", error);
			setError(error);
		}
	}, [id, jwtToken]);

	useEffect(() => {
		if (id && jwtToken) {
			fetchRecords();
		}
	}, [id, jwtToken, fetchRecords]);

	const hasPendingWithdrawal = () => {
		return peopleData.some((item) => {
			const record = item?.partnerRecordDTO;

			if (!record) {
				return false;
			}

			return (
				record.recordType === "WITHDRAWAL" &&
				isPendingRecordStatus(record.recordStatus, record.recordType)
			);
		});
	};

	const handleOpenWithdrawalModal = () => {
		if (hasPendingWithdrawal()) {
			setErrorMessage(
				"É necessário finalizar os pedidos de levantamento pendentes antes de realizar um novo pedido."
			);

			window.setTimeout(() => {
				setErrorMessage("");
			}, 5000);

			return;
		}

		setErrorMessage("");
		setIsWithdrawalModalOpen(true);
	};

	const handleCloseWithdrawalModal = () => {
		setIsWithdrawalModalOpen(false);
	};

	const handleOpenApproveModal = (record) => {
		setSelectedPendingRecord(record);
		setApprovalError("");
		setApprovalSuccess("");
		setIsApproveModalOpen(true);
	};

	const handleCloseApproveModal = () => {
		if (approving) return;

		setIsApproveModalOpen(false);
		setSelectedPendingRecord(null);
		setApprovalError("");
		setApprovalSuccess("");
	};

	const approvePendingWithdrawal = async () => {
		if (!selectedPendingRecord?.recordID) {
			setApprovalError("Não foi possível identificar o pedido de levantamento.");
			return;
		}

		try {
			setApproving(true);
			setApprovalError("");
			setApprovalSuccess("");

			const response = await fetch(
				`${URL}/api/partners/${id}/records/${selectedPendingRecord.recordID}/complete`,
				{
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: `Bearer ${jwtToken}`,
					},
					body: JSON.stringify({
						status: "COMPLETED",
					}),
				}
			);

			const responseText = await response.text();

			if (!response.ok) {
				let message = "Não foi possível aprovar o pedido de levantamento.";

				try {
					const parsed = JSON.parse(responseText);
					message = parsed?.message || message;
				} catch {
					message = responseText || message;
				}

				throw new Error(message);
			}

			setApprovalSuccess("Pedido de levantamento aprovado com sucesso.");

			await fetchRecords();

			window.setTimeout(() => {
				handleCloseApproveModal();
			}, 900);
		} catch (error) {
			console.error("Erro ao aprovar levantamento:", error);
			setApprovalError(
				error?.message || "Não foi possível aprovar o pedido de levantamento."
			);
		} finally {
			setApproving(false);
		}
	};

	const handleDownloadClick = (record) => {
		const partnerID = record?.partnerID || id;
		const endpoint = `${URL}/api/export-csv/withdrawal/partners/${partnerID}`;

		fetch(endpoint, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${jwtToken}`,
			},
		})
			.then((response) => {
				if (!response.ok) {
					return response.text().then((text) => {
						console.error("Error Response:", text);
						throw new Error("Failed to download CSV");
					});
				}

				return response.blob().then((blob) => ({ blob }));
			})
			.then(({ blob }) => {
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");

				link.href = url;
				link.setAttribute("download", `withdrawal-${partnerID}.csv`);

				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				window.URL.revokeObjectURL(url);
			})
			.catch((error) => {
				console.error("Error downloading CSV:", error);
			});
	};

	const handleSearch = (event) => {
		const term = event.target.value.toLowerCase();

		setSearchTerm(term);

		if (!term) {
			setPeopleData(originalPeople);
			setCurrentPage(1);
			return;
		}

		const filteredData = originalPeople.filter((item) => {
			const record = item?.partnerRecordDTO;
			const meta = item?.transactionMetaDataDTO;

			if (!record) {
				return false;
			}

			const statusLabel = getRecordStatusLabel(
				record.recordStatus,
				record.recordType
			);

			const statusNormalized = normalizeRecordStatus(
				record.recordStatus,
				record.recordType
			);

			return (
				String(record.recordID || "").toLowerCase().includes(term) ||
				String(record.recordType || "").toLowerCase().includes(term) ||
				String(statusLabel || "").toLowerCase().includes(term) ||
				String(statusNormalized || "").toLowerCase().includes(term) ||
				String(meta?.paymentMethod || "").toLowerCase().includes(term) ||
				String(meta?.customerID || "").toLowerCase().includes(term)
			);
		});

		setPeopleData(filteredData);
		setCurrentPage(1);
	};

	const formatMoney = (value) => {
		const numberValue = Number(value || 0);
		return `${numberValue.toFixed(2).replace(".", ",")}\u00A0€`;
	};

	const indexOfLastPerson = currentPage * peoplePerPage;
	const indexOfFirstPerson = indexOfLastPerson - peoplePerPage;
	const currentPeople = peopleData.slice(indexOfFirstPerson, indexOfLastPerson);

	const paginate = (pageNumber) => setCurrentPage(pageNumber);

	return (
		<div className="min-w-0 overflow-hidden rounded-3xl bg-white p-5 shadow-xl ring-1 ring-gray-100 md:p-7">
			<div>
				<div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
					<div className="flex">
						<div className="flex-shrink-0">
							<ExclamationCircleIcon
								className="h-5 w-5 text-yellow-400"
								aria-hidden="true"
							/>
						</div>

						<div className="ml-3">
							<p className="text-sm text-yellow-700">
								Caso tenha algum{" "}
								<strong>
									<span className="italic">status pendente</span>, clique no{" "}
									<span className="inline-flex items-center">
										<span>botão</span>{" "}
										<CheckCircleIcon className="ml-1 h-5 w-5 text-green-cl" />
									</span>
								</strong>{" "}
								que aparecerá na tabela para confirmar o pedido.
							</p>
						</div>
					</div>
				</div>

				<div className="mt-6 sm:flex sm:items-center">
					<div className="sm:flex-auto">
						<h1 className="text-2xl font-extrabold text-gray-900">
							GESTÃO DE TRANSAÇÕES MONETÁRIAS
						</h1>

						<p className="-mt-1 text-sm text-gray-700">
							Todas as transações sobre pedidos de transações encontram-se aqui.
						</p>
					</div>

					<div className="relative mt-4 sm:ml-4 sm:mt-0 sm:flex-none">
						<button
							type="button"
							onClick={handleOpenWithdrawalModal}
							className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-green-cl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-green-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 active:translate-y-0"
						>
							<BanknotesIcon className="h-5 w-5" />
							Levantar saldo
						</button>

						{errorMessage && (
							<p className="absolute right-0 top-full z-20 mt-2 w-[420px] max-w-[calc(100vw-32px)] rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 shadow-md">
								{errorMessage}
							</p>
						)}
					</div>
				</div>

				<div className="relative mt-8">
					<input
						type="text"
						placeholder="Pesquise por ID, tipo, status, cliente ou método"
						className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-10 leading-normal outline-none focus:border-green-900 focus:ring-transparent"
						onChange={handleSearch}
						value={searchTerm}
					/>

					<MagnifyingGlassIcon className="absolute left-3 top-1/2 h-6 w-6 -translate-y-1/2 text-gray-400" />
				</div>

				{error && (
					<div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
						Não foi possível carregar os registos deste parceiro.
					</div>
				)}

				<div name="TransactionTableAdminDiv" className="mt-8">
					<div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
						<table
							name="TransactionTableAdmin"
							className="min-w-[980px] w-full table-fixed divide-y divide-gray-200"
						>
							<colgroup>
								<col className="w-[27%]" />
								<col className="w-[15%]" />
								<col className="w-[15%]" />
								<col className="w-[12%]" />
								<col className="w-[18%]" />
								<col className="w-[13%]" />
							</colgroup>

							<thead className="bg-gray-50">
							<tr>
								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									Pedido
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									Pagamento
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									Transações
								</th>

								<th className="px-4 py-4 text-center text-xs font-black uppercase text-gray-900">
									Tipo
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									Valores
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									Status
								</th>
							</tr>
							</thead>

							<tbody className="divide-y divide-gray-200 bg-white">
							{currentPeople.length === 0 && (
								<tr>
									<td
										colSpan={6}
										className="px-6 py-8 text-center text-sm text-gray-500"
									>
										Nenhum registo encontrado.
									</td>
								</tr>
							)}

							{currentPeople.map((item) => {
								const record = item.partnerRecordDTO;
								const meta = item.transactionMetaDataDTO;

								const isWithdrawal = record.recordType === "WITHDRAWAL";
								const isPending = isPendingRecordStatus(
									record.recordStatus,
									record.recordType
								);

								return (
									<tr key={record.recordID} className="hover:bg-gray-50">
										<td className="px-4 py-4 text-sm">
											<div className="flex min-w-0 items-center gap-3">
												<FontAwesomeIcon
													icon={faLandmarkFlag}
													className="h-5 w-5 flex-shrink-0 text-gray-400"
												/>

												<div className="min-w-0">
													<p
														className="truncate font-medium text-gray-900"
														title={record.recordID}
													>
														{record.recordID}
													</p>

													<p className="mt-0.5 truncate text-xs text-gray-500">
														{record.recordDate}
													</p>
												</div>
											</div>
										</td>

										<td className="px-4 py-4 text-sm text-gray-600">
											<p className="truncate font-semibold text-gray-700">
												{meta?.paymentMethod || "-"}
											</p>

											<p className="mt-0.5 truncate text-xs text-gray-400">
												Cliente: {meta?.customerID || "-"}
											</p>
										</td>

										<td className="px-4 py-4 text-sm text-gray-600">
											<p className="whitespace-nowrap">
													<span className="font-bold text-gray-800">
														{meta?.transactionQuantity || 0}
													</span>{" "}
												trans.
											</p>

											<p className="mt-0.5 whitespace-nowrap text-xs text-gray-500">
												{meta?.transactionVolume !== undefined &&
												meta?.transactionVolume !== null
													? formatMoney(meta.transactionVolume)
													: "-"}
											</p>
										</td>

										<td className="px-4 py-4 text-center text-sm">
											<div className="flex justify-center">
													<span className="inline-flex items-center justify-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-200">
														{isWithdrawal ? "Levantamento" : "Depósito"}
													</span>
											</div>
										</td>

										<td className="px-4 py-4 text-sm">
											<p className="whitespace-nowrap font-black text-gray-900">
												{isWithdrawal ? "-" : "+"}
												{formatMoney(record.recordAmount)}
											</p>

											<p className="mt-0.5 whitespace-nowrap text-xs font-semibold text-gray-500">
												Saldo: {formatMoney(record.balance)}
											</p>
										</td>

										<td className="px-4 py-4 text-left text-sm">
											<div className="flex items-center justify-start gap-2">
												<RecordStatusBadge
													status={record.recordStatus}
													recordType={record.recordType}
												/>

												{isWithdrawal && isPending && (
													<>
														<button
															type="button"
															onClick={() => handleOpenApproveModal(record)}
															title="Confirmar levantamento"
															className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-green-600 transition hover:bg-green-50 hover:text-green-800"
														>
															<CheckCircleIcon className="h-5 w-5" />
														</button>

														<button
															type="button"
															onClick={() => handleDownloadClick(record)}
															title="Descarregar CSV"
															className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-blue-500 transition hover:bg-blue-50 hover:text-blue-700"
														>
															<FontAwesomeIcon icon={faDownload} />
														</button>
													</>
												)}
											</div>
										</td>
									</tr>
								);
							})}
							</tbody>
						</table>

						<div className="border-t border-gray-100 px-6 py-4 text-sm text-gray-500">
							Exibindo {peopleData.length > 0 ? indexOfFirstPerson + 1 : 0} a{" "}
							{Math.min(indexOfLastPerson, peopleData.length)} de{" "}
							{peopleData.length} resultados
						</div>

						{peopleData.length > peoplePerPage && (
							<Pagination
								postsPerPage={peoplePerPage}
								totalPosts={peopleData.length}
								paginate={paginate}
								currentPage={currentPage}
							/>
						)}
					</div>
				</div>
			</div>

			{isWithdrawalModalOpen && (
				<div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
					<button
						type="button"
						aria-label="Fechar modal"
						onClick={handleCloseWithdrawalModal}
						className="absolute inset-0 cursor-default bg-slate-900/55 backdrop-blur-[1px]"
					/>

					<div className="relative z-10 w-full max-w-[520px] rounded-2xl bg-white shadow-2xl">
						<button
							type="button"
							onClick={handleCloseWithdrawalModal}
							aria-label="Fechar"
							className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-cl"
						>
							<XMarkIcon className="h-5 w-5" />
						</button>

						<div className="p-6 pt-8">
							<WithdrawalFormAdmin />
						</div>
					</div>
				</div>
			)}

			{isApproveModalOpen && (
				<div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
					<button
						type="button"
						aria-label="Fechar modal"
						onClick={handleCloseApproveModal}
						className="absolute inset-0 cursor-default bg-slate-900/55 backdrop-blur-[1px]"
					/>

					<div className="relative z-10 w-full max-w-[680px] rounded-2xl bg-white shadow-2xl">
						<button
							type="button"
							onClick={handleCloseApproveModal}
							aria-label="Fechar"
							disabled={approving}
							className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-cl disabled:cursor-not-allowed disabled:opacity-50"
						>
							<XMarkIcon className="h-5 w-5" />
						</button>

						<div className="p-7">
							<h2 className="pr-10 text-2xl font-black uppercase text-gray-950">
								Aprovação de pedido de levantamento
							</h2>

							<p className="mt-2 text-sm text-gray-700">
								Como <strong>administrador</strong>, tem a certeza que pretende
								aprovar este pedido de levantamento?
							</p>

							{approvalSuccess && (
								<div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
									{approvalSuccess}
								</div>
							)}

							{approvalError && (
								<div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
									{approvalError}
								</div>
							)}

							<button
								type="button"
								onClick={approvePendingWithdrawal}
								disabled={approving}
								className="mt-5 w-full rounded-xl bg-green-cl px-5 py-3 text-sm font-black uppercase text-white shadow-sm transition hover:bg-green-900 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{approving
									? "A aprovar..."
									: "Sim, quero confirmar e aprovar"}
							</button>

							<div className="mt-6 rounded-xl bg-gray-50 p-4">
								<p className="text-xs font-black uppercase tracking-wide text-gray-500">
									ID de pedido
								</p>

								<p className="mt-1 break-all text-sm font-semibold text-gray-900">
									{selectedPendingRecord?.recordID || "-"}
								</p>

								<div className="mt-4 grid gap-3 sm:grid-cols-3">
									<div>
										<p className="text-xs font-black uppercase tracking-wide text-gray-500">
											Tipo
										</p>
										<p className="mt-1 text-sm font-semibold text-gray-900">
											{selectedPendingRecord?.recordType === "WITHDRAWAL"
												? "Levantamento"
												: selectedPendingRecord?.recordType || "-"}
										</p>
									</div>

									<div>
										<p className="text-xs font-black uppercase tracking-wide text-gray-500">
											Valor
										</p>
										<p className="mt-1 text-sm font-semibold text-gray-900">
											{formatMoney(selectedPendingRecord?.recordAmount || 0)}
										</p>
									</div>

									<div>
										<p className="text-xs font-black uppercase tracking-wide text-gray-500">
											Estado atual
										</p>
										<p className="mt-1">
											<RecordStatusBadge
												status={selectedPendingRecord?.recordStatus}
												recordType={selectedPendingRecord?.recordType}
											/>
										</p>
									</div>
								</div>
							</div>

							<button
								type="button"
								onClick={handleCloseApproveModal}
								disabled={approving}
								className="mt-4 w-full rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Cancelar
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
